import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, ZoomIn } from 'lucide-react';
import { DbSwitch } from './DbSwitch';
import { AI_IMG, genLayout, type GenEntry } from './realImages';
import { GEN_MODELS } from './models';
import { OTHER_DIRECTION, directionLabel } from './viewDirections';
import { useLanguage } from '../../contexts/LanguageContext';

// The two cells that make a part a GENERATED part: the photographs drawn for it, and the
// pictures they were drawn from.
//
// They were a table of their own — one column per part, a schema row, a generated row, a
// context row — beside a parts table with one column per part and a schema row of its own.
// Two tables, the same columns, the same crops, in two views of the same phase. They are
// rows of the parts table now (see PartsTable), and these are the cells it puts in them.
type ViewCol = {
  key: string;
  name: string;
  // The context pictures offered to the generator (finished-product hero + the notice's
  // listing photos), each with whether it is currently selected. Empty for hardware /
  // tools, or a manual with no product photo.
  // `view` is the direction Phase 1 filed that photograph under, drawn under it here as
  // the same label. Undefined for a picture with no answer (and for every context row of
  // the pipeline that has no directions at all).
  // WHICH PICTURES GO WITH THE CALL. `at` is a picture's place in it, 1-based, where the
  // call takes several and the order matters — Phase 5's written engine numbers them in the
  // prompt, so the tile carries the same number. 0 or absent draws none, which is every
  // caller whose pictures are a set rather than a list.
  context: { url: string; selected: boolean; view?: string; at?: number }[];
  // Every photo generated for this part (both models, every regenerate), oldest to newest
  // — browsable in the cell. Model-agnostic, so toggling the model never blanks it.
  history: GenEntry[];
  busy: boolean;
  // False when there is no crop or no key to store under — nothing to generate from.
  canGenerate: boolean;
  // Which of this part's history versions are in the Parts DB (index -> true for the ones
  // that are, NULL for the ones not known yet — a spinner, never an OFF that would add), and
  // whether the part CAN be added at all (only ones already in the catalog).
  dbRefs: Record<number, boolean | null>;
  canAddToDb: boolean;
};

export type PartImageCol = ViewCol;

// The pictures offered to the generator, in a small wrapping grid. Click one to select /
// deselect it (emerald ring = selected); only selected ones are sent. Empty for hardware /
// tools and manuals with no product photo.
export function ContextCell({ context, onZoom, onToggleContext, gridCls = 'grid-cols-2', maxAcross }: {
  context: PartImageCol['context'];
  onZoom: (url: string) => void;
  // The choice is shared across every column, since they all offer the same pictures.
  onToggleContext: (url: string) => void;
  // HOW MANY ACROSS, because the same tiles are drawn in two widths now. Two in a table
  // cell (below); a wide row in Phase 5's whole-product panel, which is not a column of a
  // table and has the page to spread over. A whole class name from the caller rather than
  // a number, so Tailwind can see both of them written down.
  gridCls?: string;
  // OR AT MOST THIS MANY ACROSS, in rows of EVEN length and centred — for a row that stands
  // alone (Phase 5's product pictures) rather than lining up with the cells beside it. Nine
  // pictures six across was a full row over a stub of three hanging left; five over four
  // reads as one set. Every tile is w-16, so the width of `across` of them is known.
  maxAcross?: number;
}) {
  const { t } = useLanguage();
  if (!context.length) {
    return <span className="text-slate-300">—</span>;
  }
  const across = maxAcross && Math.ceil(context.length / Math.ceil(context.length / maxAcross));
  return (
    // TWO ACROSS, like the listing crops above it: a column of ten tiles is a row tall
    // enough to bury everything under it, and these two rows are read together — the
    // photographs a part was found in, then the photographs its generator is given.
    //
    // A grid rather than a wrapping flex, for the reason ListingCropCell gives: wrapping
    // asks each picture how wide it is, so the count per line drifts between cells and the
    // tiles stop lining up across parts. The column is what widens to hold two.
    <div
      className={across ? 'flex flex-wrap justify-center gap-1.5' : `grid ${gridCls} justify-items-center gap-1.5`}
      // 4rem a tile, 0.375rem (gap-1.5) between two.
      style={across ? { width: `calc(${across} * 4rem + ${across - 1} * 0.375rem)`, maxWidth: '100%' } : undefined}
    >
      {context.map((ci) => (
        // Picture and caption in one column, so the label dims with the thumbnail it
        // belongs to when the picture is turned off — the row says what is being SENT, and
        // a full-strength label over a faded picture would be the one part of it still
        // claiming otherwise.
        <div key={ci.url} className="flex w-16 flex-col items-center gap-0.5">
          <div className="relative">
            {/* The picture toggles it in or out of the call; zooming is its own corner
                button, so looking closely at a candidate cannot be mistaken for dropping
                it. */}
            <button
              type="button"
              onClick={() => onToggleContext(ci.url)}
              title={t(ci.selected ? 'notice.step3.contextOn' : 'notice.step3.contextOff')}
              className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border-2 bg-white transition-all ${
                ci.selected
                  ? 'border-blue-500'
                  : 'border-slate-200 opacity-40 hover:opacity-70'
              }`}
            >
              <img src={ci.url} alt="" className="max-h-full max-w-full object-contain p-0.5" />
            </button>
            {/* ITS NUMBER IN THE CALL. The prompt calls them "Picture 1", "Picture 2" and
                names an angle for each, so the row on screen is the list the model is given
                — and the order is the order they were pressed in, which is how "this one
                first" is said without a second control for it. */}
            {!!ci.at && (
              <span className="absolute -left-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-sm">
                {ci.at}
              </span>
            )}
            {/* Full strength even on a deselected picture: it is how you look at one to
                decide whether to put it back. */}
            <button
              type="button"
              onClick={() => onZoom(ci.url)}
              title={t('notice.pdf.zoom')}
              aria-label={t('notice.pdf.zoom')}
              className="absolute right-0.5 top-0.5 rounded-md border border-slate-300 bg-white/90 px-1 py-0.5 text-slate-500 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600"
            >
              <ZoomIn className="h-3 w-3" />
            </button>
          </div>
          {/* The same label as the Phase 1 strip, at the size this row has room for: named
              directions emerald, "other" grey and quiet. Read-only here — Phase 1 is where
              a direction is corrected, and two places to change one answer is one place to
              disagree. */}
          {ci.view && (
            <span
              title={directionLabel(ci.view)}
              className={`max-w-16 truncate text-[9px] font-semibold uppercase leading-tight tracking-wide transition-opacity ${
                ci.selected ? '' : 'opacity-40'
              } ${
                ci.view === OTHER_DIRECTION
                  ? 'text-slate-400'
                  : 'text-blue-700'
              }`}
            >
              {directionLabel(ci.view)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// One part's generated photos laid out as a version grid: from-scratch generations are
// columns; versions regenerated FROM an attempt stack under it as rows. Each carries its id,
// the model that drew it and the angle it was asked for, and clicking one selects it — which
// is what the row's Generate button then corrects. Pictures only: the button is in the row's
// label, see GenerateButton.
export function GeneratedCell({
  col,
  at,
  onSelect,
  onZoom,
  onToggleDb,
}: {
  col: PartImageCol;
  // The chosen version, by its index in the history — or null, nothing chosen. Held by the
  // TABLE and not here, because the button that acts on it is no longer in this cell: it is
  // in the row's label, and a control and the thing it corrects cannot live in two
  // components that do not share it. See PartsTable.
  at: number | null;
  onSelect: (at: number | null) => void;
  onZoom: (url: string) => void;
  onToggleDb: (historyIndex: number, image: string, added: boolean) => void | Promise<void>;
}) {
  const { t } = useLanguage();
  const { history, busy, name, dbRefs, canAddToDb } = col;
  const { cells } = useMemo(() => genLayout(history), [history]);
  // Every version at once, lineages kept together: a from-scratch image and then the
  // attempts regenerated from it, before the next lineage starts. The pager this replaced
  // could only ever show one, so which angles a part HAD was a thing you found by clicking
  // through — and the whole reason this pipeline generates several is to look at them
  // together.
  const ordered = useMemo(
    () => cells.slice().sort((a, b) => a.col - b.col || a.row - b.row),
    [cells],
  );
  // Which version's Parts-DB switch is in flight. By index, because the switch is on every
  // tile now rather than on the one selected below them.
  const [dbBusyAt, setDbBusyAt] = useState<number | null>(null);
  // Jump to the newest whenever the history grows (a fresh generation just landed). Through
  // a ref so the effect fires on the LENGTH and on nothing else: the callback is a fresh
  // closure on every render of the table, and watching it would re-select the newest version
  // each time — including the render right after somebody let go of one.
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  useEffect(() => {
    selectRef.current(history.length ? history.length - 1 : null);
  }, [history.length]);
  // Clamped against the history it indexes: the table's answer for this column can outlive
  // the versions it was about — a re-run replaces them — and an index past the end selects a
  // tile that is not there.
  const sel = at != null && at >= 0 && at < history.length ? at : null;
  // Put a version in the Parts DB, or take it out — per version, off by default.
  const toggleDb = async (index: number) => {
    const entry = history[index];
    if (!entry || dbBusyAt != null || dbRefs[index] === null) return;
    setDbBusyAt(index);
    try {
      await onToggleDb(index, entry.image, dbRefs[index] === true);
    } finally {
      setDbBusyAt(null);
    }
  };
  const modelLabelOf = (id: string) => GEN_MODELS.find((m) => m.id === id)?.label ?? id;
  // Both caption lines, in one place: they are two facts of the same kind about the same
  // version, and styling them apart is what made one of them read as something else.
  const captionCls =
    'max-w-20 truncate text-[9px] font-semibold uppercase leading-tight tracking-wide text-blue-700';

  return (
    <div className="flex flex-wrap items-start justify-center gap-1.5">
        {ordered.map((c) => (
          <figure key={c.index} className="flex w-20 flex-col items-center gap-0.5">
            <div className="relative">
              {/* The picture is the SELECT button; zooming is its own corner button, so
                  looking closely at a version cannot be mistaken for choosing it — the
                  same split the phase 1 strip makes. */}
              <button
                type="button"
                onClick={() => onSelect(c.index === sel ? null : c.index)}
                aria-pressed={c.index === sel}
                title={name}
                className={`flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border-2 bg-white transition-all ${
                  c.index === sel
                    ? 'border-blue-500'
                    : 'border-slate-200 opacity-50 hover:opacity-90'
                }`}
              >
                <img src={c.entry.image} alt={name} className="max-h-full max-w-full object-contain p-0.5" />
              </button>
              <button
                type="button"
                onClick={() => onZoom(c.entry.image)}
                title={t('notice.pdf.zoom')}
                aria-label={t('notice.pdf.zoom')}
                className="absolute right-0.5 top-0.5 rounded-md border border-slate-300 bg-white/90 px-1 py-0.5 text-slate-500 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600"
              >
                <ZoomIn className="h-3 w-3" />
              </button>
              {/* In the Parts DB — the mark AND the switch, on every version rather than on
                  the selected one. It was a green badge that appeared on the ones already
                  in, with the control for it a separate button under the whole cell: to
                  add a version you first selected it, then read a button somewhere else to
                  find out what state it was in. Two places for one answer, and the badge
                  was the only one you were looking at.

                  Drawn in both states, so the mark is countable: grey means not in the
                  database, and clicking it puts it there. Its own button, a sibling of the
                  picture rather than inside it, so the click that toggles is not also the
                  click that selects.

                  Only for parts the catalog knows — with no item there is nothing to
                  attach a photo to, and the table's IN DB row is where that is fixed. */}
              {canAddToDb && (
                <DbSwitch
                  on={dbRefs[c.index] === true}
                  busy={dbBusyAt === c.index || dbRefs[c.index] === null}
                  disabled={dbBusyAt != null}
                  title={t(dbRefs[c.index] === true ? 'notice.step3.addedToDb' : 'notice.step3.addToDb')}
                  className="absolute left-0.5 top-0.5"
                  onToggle={() => void toggleDb(c.index)}
                />
              )}
            </div>
            {/* ITS NAME, first, and in slate where the two facts under it are emerald: a
                model and an angle are things TRUE of this picture, and this is what to
                call it. The number is the notice_part_images row — the same id the server,
                the Parts DB and a bug report all mean.

                "AIimg" and not a bare "#12", because this page already numbers two other
                things: the manual's runs (1 to 12, in its own bar) and a part's meshes.
                Absent on an attempt that never reached the server, which has no id to
                print rather than an id worth hiding. */}
            {c.entry.id != null && (
              <figcaption className="max-w-20 truncate text-[9px] font-semibold uppercase leading-tight tracking-wide text-slate-400">
                {AI_IMG} #{c.entry.id}
              </figcaption>
            )}
            {/* Model first, angle under it. Every version has a model; only some have an
                angle, so the line that is always there is the one that sits against the
                picture — otherwise the caption's first line moves up and down the tile
                depending on which fact happens to be missing. */}
            <figcaption
              title={modelLabelOf(c.entry.model)}
              className={captionCls}
            >
              {modelLabelOf(c.entry.model)}
            </figcaption>
            {/* The angle it was ASKED for. Nothing when none was (the whole LLM pipeline,
                and anything made before it was recorded): a blank says "not asked", where
                a default "Front" would say the front was chosen when nothing was. */}
            {c.entry.view && (
              <figcaption className={captionCls}>{directionLabel(c.entry.view)}</figcaption>
            )}
          </figure>
        ))}

        {/* A generation in flight, as one more tile: the part has no new picture yet, and
            a spinner over the ones it already has would claim they were being replaced. */}
        {busy && (
          <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-white">
            <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          </div>
        )}

        {/* Nothing generated and nothing running: one empty frame, so the row keeps its
            shape and the cell reads as "not yet" rather than as broken. */}
        {!ordered.length && !busy && (
          <div className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-200 bg-white" />
        )}
    </div>
  );
}

// THE CONTROL THAT MAKES A PICTURE, wherever the part it is about happens to be drawn.
//
// With a version selected it corrects THAT one; with none, it makes a fresh one — the only
// way to start a new lineage once a part has any image at all. One button either way, and
// it says "Generate" in both: the difference is the selection above it, which is on screen,
// and the icon, and the modal it opens names the two apart before anything is paid for. It
// used to say "Regenerate" whenever a tile was lit, which made the row's one control change
// its name under the pointer for a distinction the dialog behind it repeats.
//
// Its own component because it is drawn in two places for one reason: with a single part on
// screen the row IS that part, so the button belongs in the row's label under the title —
// with the prompt it sends under it — and under "All parts" every column is a different part
// and needs its own. See PartsTable.
export function GenerateButton({ col, at, onRegenerate }: {
  col: PartImageCol;
  at: number | null;
  onRegenerate: (currentImage?: string) => void;
}) {
  const { t } = useLanguage();
  const { history, busy, canGenerate } = col;
  const current = at != null && at >= 0 && at < history.length ? history[at] : undefined;
  const label = t('notice.step3.generateNew');
  return (
    <button
      type="button"
      onClick={() => onRegenerate(current?.image)}
      disabled={!canGenerate || busy}
      // The name stays one word; WHICH of the two this press is, is said here.
      title={t(current ? 'notice.step3.regenerate' : 'notice.step3.generateNew')}
      aria-label={t(current ? 'notice.step3.regenerate' : 'notice.step3.generateNew')}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium normal-case tracking-normal text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600 disabled:opacity-40"
    >
      {current ? (
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
      ) : (
        // Sparkles, not the redo arrows: nothing is being redone. Still spins-in-place
        // as the redo icon does while a generation is running, so the two states of the
        // button read the same way.
        <Sparkles className={`h-3.5 w-3.5 ${busy ? 'animate-pulse' : ''}`} />
      )}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}
